/**
 * Voortgang van gekoppelde kinderen ophalen
 * ─────────────────────────────────────────
 *
 * Een ouder of leerkracht heeft de voortgang van een kind niet op zijn eigen
 * apparaat staan. De portalen lezen `multiAccountState.learnerData[id]`, en dat
 * is daar leeg — niet omdat de data ontbreekt, maar omdat ze op de server
 * staat, in `solo_progress`.
 *
 * Tot migratie 0016 was dat ook niet op te lossen: die tabel had vier policies
 * die allemaal `learner_id = auth.uid()` eisten. Alleen het kind zelf mocht
 * zijn rij lezen. Sinds 0016 is er één extra SELECT-policy die
 * `can_view_learner()` gebruikt, en die regelt in één keer:
 *
 *   - het kind zelf                    → ja
 *   - een gekoppelde ouder             → ja
 *   - de leerkracht van zijn klas      → ja
 *   - een admin zonder toegangsvenster → nee
 *   - een admin mét gelogd venster     → ja
 *
 * Dat is de reden dat hieronder geen enkele autorisatiecontrole staat. Die
 * hoort niet in de client: wat de database niet teruggeeft, kan geen enkele
 * bug in dit bestand alsnog zichtbaar maken. Een filter hier zou hooguit een
 * tweede, afwijkende waarheid worden.
 *
 * WAT HIER NIET IN ZIT
 *
 * Toetsuitslagen en rapporten. Die vallen onder de muur van 0010 en staan voor
 * een solo-kind uitsluitend op zijn eigen apparaat. Een ouder ziet dus wel de
 * oefenvoortgang en niet de toetsgeschiedenis, ook ná toestemming — die moet
 * eerst worden ingehaald vanaf het apparaat van het kind. Zie
 * `docs/specs/SOLO_TO_LINKED_TRANSITION.md` §4.
 */

import { supabase } from './client'

/** Wat er uit `solo_progress.payload` komt: de allowlist uit 0009. */
export type ProgressPayload = Record<string, unknown>

/**
 * Haalt de voortgang op van meerdere leerlingen tegelijk.
 *
 * Bewust één query in plaats van één per kind: een ouder met drie kinderen zou
 * anders drie keer over het netwerk, en een leerkracht met dertig leerlingen
 * dertig keer. Dat is precies het patroon dat een klasoverzicht traag maakt.
 *
 * Ontbrekende of afgeschermde leerlingen komen simpelweg niet terug. Dat is
 * geen fout: een kind dat nog nooit heeft geoefend heeft geen rij, en dat is
 * niet te onderscheiden van — en hoeft niet te worden onderscheiden van — een
 * kind waar je niet bij mag.
 */
export async function fetchProgressForLearners(
  learnerIds: string[],
): Promise<Record<string, ProgressPayload>> {
  if (learnerIds.length === 0) return {}

  const { data, error } = await supabase
    .from('solo_progress')
    .select('learner_id, payload')
    .in('learner_id', learnerIds)

  if (error || !data) {
    // Niet gooien. Een portaal dat crasht omdat de wifi hapert is erger dan
    // een portaal dat even geen cijfers toont; de aanroeper valt terug op wat
    // er lokaal staat.
    return {}
  }

  const byLearner: Record<string, ProgressPayload> = {}
  for (const row of data as Array<{
    learner_id: string
    payload: ProgressPayload | null
  }>) {
    if (row.payload) byLearner[row.learner_id] = row.payload
  }
  return byLearner
}
